import { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { SeasonContext } from '../lib/seasonContext.ts';
import { points, record, signedMoney } from '../lib/format.ts';
import { NoGamesYet } from '../components/EmptyState.tsx';
import { ManagerLink } from '../components/ManagerLink.tsx';
import { AwardStrip } from '../components/AwardStrip.tsx';

/**
 * Where the season stands, in three passes over the same fortnight of football:
 * the table, the side pots being won off it, and the week-by-week grid the
 * table is built from.
 *
 * These were three tabs. They are one page because nobody ever wanted just one
 * of them — you check the table, then check whether the points you scored are
 * winning you anything, then look at the grid to see whether last week was a
 * fluke. Scrolling is cheaper than navigating.
 */
export function Standings() {
  const { stored, nameOf } = useOutletContext<SeasonContext>();
  const { teams, regularSeasonWeeks, results, season, owners } = stored.bundle.data;
  const { money } = stored.bundle;

  // "Started" means any game has been played or is live, not that a whole week
  // is final. A live Week 1 should still show the table, not the empty state.
  const started = results.length > 0;

  return (
    <div className="space-y-10">
      <div>
        <p className="eyebrow">
          {season} · {regularSeasonWeeks}-week season
        </p>
        <h1 className="mt-1 font-display text-3xl font-black uppercase tracking-tight">
          Standings
        </h1>
      </div>

      {started ? (
        <Table teams={teams} playoffLine={stored.bundle.data.playoffTeamCount} nameOf={nameOf} />
      ) : (
        <NoGamesYet season={season} />
      )}

      {/* Not gated on games having started. The pots exist from the moment the
          league does, and an award with its own page — the Clown's draft board,
          which is at its most useful in the fortnight after the draft — is now
          reached by clicking its ticket. Hiding the strip until week 1 would
          leave that page with no way in at all. */}
      <section>
        <h2 className="eyebrow mb-3">The Side Pots</h2>
        <AwardStrip awards={money.awards} config={money.config} nameOf={nameOf} />
      </section>

      {started && <Grid results={results} owners={owners} nameOf={nameOf} />}
    </div>
  );
}

/**
 * The table, but honest: alongside the record we show points for, points
 * against, and the differential ESPN buries. Sorted the way the league is
 * actually decided: wins, then points for.
 */
function Table({
  teams,
  playoffLine,
  nameOf,
}: {
  teams: SeasonContext['stored']['bundle']['data']['teams'];
  playoffLine: number;
  nameOf: (id: string) => string;
}) {
  // Wins, then points for, then alphabetically so a pre-season 0-0 table isn't
  // in arbitrary order.
  const rows = [...teams].sort(
    (a, b) =>
      b.wins - a.wins ||
      b.pointsFor - a.pointsFor ||
      nameOf(a.ownerId).localeCompare(nameOf(b.ownerId)),
  );

  return (
    <section className="space-y-3">
      <h2 className="eyebrow">The Table</h2>
      <div className="card overflow-x-auto">
        <table className="w-full min-w-[34rem] text-sm">
          <thead>
            <tr className="border-b border-felt-line text-left">
              <Th className="w-8">#</Th>
              <Th>Manager</Th>
              <Th className="text-center">Rec</Th>
              <Th className="text-right">PF</Th>
              <Th className="text-right">PA</Th>
              <Th className="text-right">Diff</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t, i) => {
              const diff = Math.round((t.pointsFor - t.pointsAgainst) * 10) / 10;
              const inPlayoffs = i < playoffLine;
              return (
                <tr
                  key={t.ownerId}
                  className={`border-b border-felt-line/60 ${
                    i === playoffLine ? 'border-t-2 border-t-brass/40' : ''
                  }`}
                >
                  <Td className="font-mono text-chalk-dim tabular">
                    <span className={inPlayoffs ? 'text-brass' : ''}>{i + 1}</span>
                  </Td>
                  <Td className="font-display font-bold">
                    <ManagerLink ownerId={t.ownerId}>{nameOf(t.ownerId)}</ManagerLink>
                  </Td>
                  <Td className="text-center font-mono tabular">
                    {record(t.wins, t.losses, t.ties)}
                  </Td>
                  <Td className="text-right font-mono tabular">{points(t.pointsFor)}</Td>
                  <Td className="text-right font-mono tabular text-chalk-dim">
                    {points(t.pointsAgainst)}
                  </Td>
                  <Td
                    className={`text-right font-mono tabular ${
                      diff > 0 ? 'text-cash' : diff < 0 ? 'text-blood' : 'text-chalk-dim'
                    }`}
                  >
                    {signedMoney(diff).replace('$', '')}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="font-mono text-[0.7rem] text-chalk-dim">
        Gold line marks the {playoffLine}-team playoff cut.
      </p>
    </section>
  );
}

/**
 * Points-for by week, heat-mapped — the spreadsheet view everyone screenshots,
 * rebuilt live. Each cell is shaded within its own week: the week's high is
 * full cash-green, the low full blood-red, everyone else interpolated. That
 * makes a big week jump off the grid the way conditional formatting did.
 */
function Grid({
  results,
  owners,
  nameOf,
}: {
  results: SeasonContext['stored']['bundle']['data']['results'];
  owners: SeasonContext['stored']['bundle']['data']['owners'];
  nameOf: (id: string) => string;
}) {
  // Every week that has any scores, final OR live. A week in progress shows its
  // current column rather than waiting for the Monday-nighter to finalize.
  const weeks = [...new Set(results.map((r) => r.week))].sort((a, b) => a - b);

  // owner -> week -> score, plus each week's min/max for shading.
  const { grid, bounds } = useMemo(() => {
    const grid = new Map<string, Map<number, number>>();
    const bounds = new Map<number, { min: number; max: number }>();
    for (const r of results) {
      if (!grid.has(r.ownerId)) grid.set(r.ownerId, new Map());
      grid.get(r.ownerId)!.set(r.week, r.score);
      const b = bounds.get(r.week) ?? { min: Infinity, max: -Infinity };
      bounds.set(r.week, { min: Math.min(b.min, r.score), max: Math.max(b.max, r.score) });
    }
    return { grid, bounds };
  }, [results]);

  const totals = owners
    .map((o) => {
      const row = grid.get(o.id);
      const total = row ? [...row.values()].reduce((a, b) => a + b, 0) : 0;
      return { ownerId: o.id, total: Math.round(total * 10) / 10 };
    })
    // Total, then name so a no-games grid isn't in arbitrary order.
    .sort((a, b) => b.total - a.total || nameOf(a.ownerId).localeCompare(nameOf(b.ownerId)));

  return (
    <section className="space-y-3">
      <div>
        <h2 className="eyebrow">Points By Week</h2>
        <p className="mt-1 max-w-lg text-sm text-chalk-dim">
          Every manager's weekly score, shaded within the week. Green is the
          week's high, red the low.
        </p>
      </div>
      <div className="card overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-felt-raised px-3 py-2 text-left font-mono text-[0.7rem] uppercase tracking-wider text-chalk-dim">
                Manager
              </th>
              <th className="px-2 py-2 text-right font-mono text-[0.7rem] uppercase tracking-wider text-chalk-dim">
                Total
              </th>
              {weeks.map((w) => (
                <th
                  key={w}
                  className="px-2 py-2 text-right font-mono text-[0.7rem] tabular text-chalk-dim"
                >
                  {w}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {totals.map(({ ownerId, total }) => (
              <tr key={ownerId} className="border-t border-felt-line/60">
                <td className="sticky left-0 z-10 whitespace-nowrap bg-felt-raised px-3 py-2 font-display font-bold">
                  {nameOf(ownerId)}
                </td>
                <td className="px-2 py-2 text-right font-mono font-bold tabular text-brass">
                  {points(total)}
                </td>
                {weeks.map((w) => {
                  const score = grid.get(ownerId)?.get(w);
                  const b = bounds.get(w)!;
                  return (
                    <td
                      key={w}
                      className="px-2 py-2 text-right font-mono tabular"
                      style={score != null ? cellShade(score, b.min, b.max) : undefined}
                    >
                      {score != null ? points(score) : '·'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Shade a cell from blood-red (week low) through neutral to cash-green (week
 * high). Alpha scales with distance from the week's median so only the extremes
 * shout.
 */
function cellShade(score: number, min: number, max: number): React.CSSProperties {
  if (max === min) return {};
  const t = (score - min) / (max - min); // 0 = low, 1 = high
  const centered = (t - 0.5) * 2; // -1 .. 1
  const alpha = Math.abs(centered) * 0.5;
  const color = centered >= 0 ? '53, 208, 127' : '229, 72, 77';
  return { backgroundColor: `rgba(${color}, ${alpha.toFixed(3)})` };
}

function Th({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      className={`px-3 py-2 font-mono text-[0.7rem] uppercase tracking-wider text-chalk-dim ${className}`}
    >
      {children}
    </th>
  );
}
function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-3 py-2.5 ${className}`}>{children}</td>;
}
